import type { Post, Keyword } from "@shared/schema";

export function daysInMonth(y: number, m: number) {
  return new Date(y, m + 1, 0).getDate();
}

export function firstDayOfMonth(y: number, m: number) {
  return new Date(y, m, 1).getDay();
}

export function dateStr(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function getPostDate(post: Post): Date | null {
  if (post.scheduledAt) return new Date(post.scheduledAt);
  if (post.publishedAt) return new Date(post.publishedAt);
  if (post.createdAt) return new Date(post.createdAt);
  return null;
}

export function getPostDateStr(post: Post): string {
  const d = getPostDate(post);
  if (!d) return "";
  return dateStr(d.getFullYear(), d.getMonth(), d.getDate());
}

export function getPostTimeStr(post: Post): string {
  const d = getPostDate(post);
  if (!d) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function computeKwScore(body: string, keywords: Keyword[]): number {
  if (!body || keywords.length === 0) return 0;
  const lower = body.toLowerCase();
  let found = 0;
  keywords.forEach(k => {
    if (lower.includes(k.keyword.toLowerCase())) found++;
  });
  return Math.min(Math.round((found / Math.min(keywords.length, 3)) * 100), 100);
}

export function computeStreak(posts: Post[]): number {
  const published = posts
    .filter(p => p.status === "published" && p.publishedAt)
    .map(p => {
      const d = new Date(p.publishedAt!);
      return dateStr(d.getFullYear(), d.getMonth(), d.getDate());
    });

  if (published.length === 0) return 0;

  const uniqueDays = Array.from(new Set(published)).sort().reverse();
  const today = new Date();
  const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = dateStr(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (!uniqueDays.includes(todayStr) && !uniqueDays.includes(yesterdayStr)) return 0;

  let streak = 0;
  let checkDate = new Date(today);
  if (!uniqueDays.includes(todayStr)) {
    checkDate = yesterday;
  }

  while (true) {
    const check = dateStr(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
    if (uniqueDays.includes(check)) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

export function getPostStatusPill(post: Post): string {
  if (post.status === "published") return "published";
  if (post.status === "scheduled") return "scheduled";
  if (post.approvalStatus === "pending") return "review";
  if (!post.mediaUrl) return "needs_image";
  return "ready";
}

export function formatPostDate(post: Post): string {
  const d = getPostDate(post);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
